import React from 'react';
import { Target, Lightbulb, Heart, Award, Users, Globe } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Target className="w-10 h-10" />,
      title: 'Results-Driven',
      description: 'Every campaign is designed with measurable outcomes and ROI in mind',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: <Lightbulb className="w-10 h-10" />,
      title: 'Innovation First',
      description: 'Staying ahead with cutting-edge strategies and emerging platforms',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: <Heart className="w-10 h-10" />,
      title: 'Client Success',
      description: 'Your growth is our priority, with dedicated support every step',
      color: 'from-orange-500 to-red-500'
    }
  ];

  const achievements = [
    { icon: <Users className="w-8 h-8" />, title: '500+ Clients', desc: 'Businesses Transformed' },
    { icon: <Globe className="w-8 h-8" />, title: 'Global Reach', desc: 'International Presence' },
    { icon: <Award className="w-8 h-8" />, title: 'Industry Leaders', desc: 'Award-Winning Team' }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              data-testid="about-title"
            >
              About <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Digital Wings</span>
            </h1>
            <p className="text-lg text-gray-600">
              Empowering businesses through innovative digital marketing strategies
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 
                className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                Our Story
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Digital Wings is a division of <strong className="text-gray-900">Iplay International</strong>, 
                  bringing together years of expertise in digital innovation and marketing excellence.
                </p>
                <p>
                  We recognized the growing need for businesses to amplify their digital presence in an increasingly 
                  competitive landscape. That's why we created Digital Wings - a specialized agency focused on 
                  delivering high-impact marketing solutions that drive real results.
                </p>
                <p>
                  Our team combines creative excellence with data-driven strategies to help businesses of all sizes 
                  reach their target audiences effectively. From viral TikTok campaigns to powerful referral networks 
                  and direct SMS outreach, we provide the tools and expertise needed to succeed in today's digital world.
                </p>
                <p>
                  With a proven track record of success across multiple industries and markets, we're committed to 
                  helping our clients achieve their business goals through innovative digital marketing strategies.
                </p>
              </div>
            </div>
            <div>
              <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-3xl p-12 shadow-2xl text-white">
                <div className="text-center">
                  <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Globe className="w-16 h-16" />
                  </div>
                  <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Global Impact
                  </h3>
                  <p className="text-white/90 text-lg">
                    Serving businesses worldwide with cutting-edge digital marketing solutions
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Parent Company */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50" data-testid="parent-company-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center px-6 py-3 bg-white rounded-full shadow-md mb-6">
              <span className="text-sm font-semibold text-gray-700">Proud Member of</span>
            </div>
            <h2 
              className="text-4xl sm:text-5xl font-bold mb-6"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              <span className="text-gray-900">Iplay</span>
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> International</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              As a division of Iplay International, we leverage extensive resources, global expertise, and 
              cutting-edge technology to deliver exceptional results for our clients. Our parent company's 
              commitment to innovation and excellence flows through everything we do at Digital Wings.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl font-bold mb-4"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Values</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div 
                key={index} 
                data-testid={`value-${index}`}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-transparent hover:-translate-y-2"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${value.color} rounded-2xl flex items-center justify-center text-white mb-6`}>
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" data-testid="achievements-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl sm:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Our Achievements
            </h2>
            <p className="text-lg text-gray-400">
              Milestones that reflect our commitment to excellence
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {achievements.map((achievement, index) => (
              <div key={index} className="text-center" data-testid={`achievement-${index}`}>
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center text-white mx-auto mb-4">
                  {achievement.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {achievement.title}
                </h3>
                <p className="text-gray-400">{achievement.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white" data-testid="mission-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-12 shadow-xl">
            <h2 
              className="text-3xl sm:text-4xl font-bold mb-6"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Mission</span>
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              To empower businesses with innovative digital marketing solutions that drive measurable growth, 
              foster authentic connections with audiences, and deliver exceptional return on investment through 
              creativity, technology, and strategic expertise.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;